// src/hooks/useFranchise.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { franchiseApi } from '@/api/franchiseApi';
import type { CreateFranchiseRequest, FranchiseFilter, FranchiseRequestFilter } from '@/types';
import { QK } from '@/hooks/queryKeys';
import { useUIStore } from '@/store/uiStore';
import { getApiErrorMessage } from '@/utils';

export function useFranchises(filters: FranchiseFilter) {
  return useQuery({
    queryKey: QK.FRANCHISE_LIST(filters),
    queryFn: () => franchiseApi.getList(filters),
    placeholderData: (prev) => prev,
    staleTime: 1000 * 60 * 2,
  });
}

export function useGuestFranchises(filters: FranchiseFilter) {
  return useQuery({
    queryKey: QK.GUEST_FRANCHISE_LIST(filters),
    queryFn: () => franchiseApi.getGuestList(filters),
    placeholderData: (prev) => prev,
    staleTime: 1000 * 60 * 2,
  });
}

export function useFranchise(id: string) {
  return useQuery({
    queryKey: QK.FRANCHISE(id),
    queryFn: () => franchiseApi.getById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateFranchise() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: (data: CreateFranchiseRequest) => franchiseApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['franchises'] });
      addToast('success', 'Đăng tin nhượng quyền thành công!');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Đăng tin thất bại')),
  });
}

export function useUpdateFranchise() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateFranchiseRequest }) => franchiseApi.update(id, data),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['franchises'] });
      qc.invalidateQueries({ queryKey: QK.FRANCHISE(variables.id) });
      addToast('success', 'Cập nhật tin nhượng quyền thành công!');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Cập nhật thất bại')),
  });
}

// ─── Nhu cầu Nhượng quyền ───

export function useFranchiseRequests(filters: FranchiseRequestFilter, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['franchiseRequests', filters],
    queryFn: () => franchiseApi.getFranchiseRequests(filters),
    placeholderData: (prev) => prev,
    staleTime: 1000 * 60 * 2,
    enabled: options?.enabled ?? true,
  });
}

export function useGuestFranchiseRequests(filters: FranchiseRequestFilter) {
  return useQuery({
    queryKey: ['guestFranchiseRequests', filters],
    queryFn: () => franchiseApi.getGuestFranchiseRequests(filters),
    placeholderData: (prev) => prev,
    staleTime: 1000 * 60 * 2,
  });
}

export function useFranchiseRequest(id: string) {
  return useQuery({
    queryKey: ['franchiseRequest', id],
    queryFn: () => franchiseApi.getFranchiseRequestById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateFranchiseRequest() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: (data: any) => franchiseApi.createFranchiseRequest(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['franchiseRequests'] });
      addToast('success', 'Đăng nhu cầu nhượng quyền thành công!');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Đăng nhu cầu thất bại')),
  });
}

export function useUpdateFranchiseRequest() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => franchiseApi.updateFranchiseRequest(id, data),
    onSuccess: (res: any, variables) => {
      qc.invalidateQueries({ queryKey: ['franchiseRequests'] });
      qc.invalidateQueries({ queryKey: ['franchiseRequest', variables.id] });
      addToast('success', 'Cập nhật nhu cầu thành công!');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Cập nhật thất bại')),
  });
}
